import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Globe, User, LogOut, Shield, Heart, Home, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/assets";

const menus = [
  {
    label: { fr: "Histoire", en: "History" },
    items: [
      { to: "/histoire", key: "nav_history" },
      { to: "/chronologie", key: "nav_timeline" },
      { to: "/carte", key: "nav_map" },
      { to: "/figures", key: "nav_figures" },
    ],
  },
  {
    label: { fr: "Culture", en: "Culture" },
    items: [
      { to: "/peuples", key: "nav_peoples" },
      { to: "/arbre", key: "nav_tree" },
      { to: "/contes", key: "nav_tales" },
      { to: "/langues", key: "nav_langues" },
      { to: "/videos", key: "nav_videos" },
    ],
  },
  {
    label: { fr: "Communauté", en: "Community" },
    items: [
      { to: "/organigramme", key: "nav_org" },
      { to: "/communaute", key: "nav_community" },
      { to: "/jeunesse", key: "nav_youth" },
    ],
  },
];

const directLinks = [{ to: "/projets", key: "nav_projects" }];

export const Nav = () => {
  const { t, tr, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || open;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-ab-black/70 backdrop-blur-xl backdrop-saturate-150 border-b border-white/5 shadow-lg shadow-black/10" : "bg-transparent"
      }`}
    >
      <div className="ab-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-3 group shrink-0">
          <img src={LOGO} alt="Anyang Beti" className="w-9 h-9 md:w-10 md:h-10 object-contain rounded-full ring-1 ring-ab-gold/40 group-hover:ring-ab-gold transition-all" />
          <span className="font-serif text-lg md:text-2xl tracking-wide text-ab-ivory whitespace-nowrap">
            ANYANG <span className="ab-gold-text font-semibold">BETI</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {menus.map((m) => (
            <div
              key={m.label.en}
              className="relative"
              onMouseEnter={() => setOpenMenu(m.label.en)}
              onMouseLeave={() => setOpenMenu(null)}
              data-testid={`nav-group-${m.label.en.toLowerCase()}`}
            >
              <button
                onClick={() => setOpenMenu(openMenu === m.label.en ? null : m.label.en)}
                className="flex items-center gap-1 text-sm tracking-wide text-ab-ivory/80 hover:text-ab-gold transition-colors py-2"
              >
                {tr(m.label)}
                <ChevronDown size={14} className={`transition-transform duration-200 ${openMenu === m.label.en ? "rotate-180" : ""}`} />
              </button>
              {openMenu === m.label.en && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50">
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.7 }}
                    className="bg-ab-ebony/85 backdrop-blur-2xl backdrop-saturate-150 border border-white/10 rounded-xl py-2 min-w-[210px] shadow-2xl shadow-black/50"
                  >
                    {m.items.map((it) => (
                      <Link
                        key={it.to}
                        to={it.to}
                        onClick={() => setOpenMenu(null)}
                        data-testid={`nav-link-${it.key}`}
                        className="block px-5 py-2.5 text-sm text-ab-ivory/80 hover:text-ab-gold hover:bg-ab-gold/5 transition-colors whitespace-nowrap"
                      >
                        {t(it.key)}
                      </Link>
                    ))}
                  </motion.div>
                </div>
              )}
            </div>
          ))}
          {directLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className="ab-link-underline text-sm tracking-wide text-ab-ivory/80 hover:text-ab-ivory transition-colors"
            >
              {t(l.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {!isHome && (
            <Link
              to="/"
              data-testid="back-home-btn"
              aria-label={t("nav_home")}
              title={t("nav_home")}
              className="flex items-center justify-center gap-1.5 w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-2.5 rounded-full border border-ab-gold/30 text-ab-gold hover:border-ab-gold hover:bg-ab-gold/10 transition-colors shrink-0"
            >
              <Home size={18} /> <span className="hidden xl:inline text-xs tracking-widest uppercase">{t("nav_home")}</span>
            </Link>
          )}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1 text-xs tracking-widest uppercase text-ab-gold border border-ab-gold/30 px-3 py-2.5 min-h-[44px] hover:border-ab-gold transition-colors"
          >
            <Globe size={13} /> {lang}
          </button>

          <Link to="/don" data-testid="nav-donate" className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-ab-gold text-ab-black px-3 py-1.5 text-[0.7rem] tracking-widest uppercase font-semibold hover:bg-ab-gold-2 transition-colors">
            <Heart size={13} /> {t("nav_donate")}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {(user.role === "admin" || user.role === "super_admin") && (
                <Link to="/admin" data-testid="nav-admin" className="ab-btn-ghost !py-2 !px-3 text-[0.7rem]">
                  <Shield size={14} /> {t("nav_admin")}
                </Link>
              )}
              <Link to="/espace-membre" data-testid="nav-member" className="ab-btn-ghost !py-2 !px-3 text-[0.7rem]">
                <User size={14} /> {t("nav_member")}
              </Link>
              <button onClick={logout} data-testid="nav-logout" title={t("nav_logout")} className="text-ab-ivory/70 hover:text-ab-gold p-2">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/connexion" data-testid="nav-login" className="ab-btn-ghost !py-2 !px-3 text-[0.7rem]">
                {t("nav_login")}
              </Link>
              <Link to="/adhesion" data-testid="nav-join" className="ab-btn-primary !py-2 !px-4 text-[0.7rem]">
                {t("nav_join")}
              </Link>
            </div>
          )}

          <button className="lg:hidden text-ab-ivory p-2.5 w-11 h-11 flex items-center justify-center" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
          className="lg:hidden bg-ab-black/95 backdrop-blur-2xl backdrop-saturate-150 border-t border-white/5 px-6 py-6 space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
          data-testid="mobile-menu"
        >
          <Link to="/" onClick={() => setOpen(false)} data-testid="mobile-link-home" className="flex items-center gap-2 text-ab-gold py-2 text-base font-semibold">
            <Home size={16} /> {t("nav_home")}
          </Link>
          {menus.map((m) => (
            <div key={m.label.en} className="border-b border-ab-gold/5 pb-3">
              <p className="text-ab-gold text-[0.7rem] tracking-widest uppercase mb-1">{tr(m.label)}</p>
              {m.items.map((it) => (
                <Link key={it.to} to={it.to} onClick={() => setOpen(false)} data-testid={`mobile-link-${it.key}`} className="block text-ab-ivory/90 py-2 text-base">
                  {t(it.key)}
                </Link>
              ))}
            </div>
          ))}
          {directLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-link-${l.key}`} className="block text-ab-ivory/90 py-2 text-base">
              {t(l.key)}
            </Link>
          ))}
          {[
            ["/manifeste", t("nav_manifeste"), "mobile-link-nav_manifeste"],
            ["/frise", t("nav_frise"), "mobile-link-nav_frise"],
            ["/bibliographie", t("nav_bibliography"), "mobile-link-nav_bibliography"],
            ["/a-propos", t("nav_about"), "mobile-link-nav_about"],
            ["/gouvernance", "Gouvernance", "mobile-link-gouvernance"],
            ["/contact", "Contact", "mobile-link-contact"],
          ].map(([to, label, tid]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={tid} className="block text-ab-ivory/90 py-2 text-base">
              {label}
            </Link>
          ))}
          <div className="pt-4 border-t border-ab-gold/10 flex flex-col gap-3">
            <button onClick={() => navigate("/don")} className="ab-btn-primary justify-center"><Heart size={15} /> {t("nav_donate")}</button>
            {user ? (
              <>
                {(user.role === "admin" || user.role === "super_admin") && (
                  <button onClick={() => navigate("/admin")} className="ab-btn-ghost justify-center">{t("nav_admin")}</button>
                )}
                <button onClick={() => navigate("/espace-membre")} className="ab-btn-ghost justify-center">{t("nav_member")}</button>
                <button onClick={logout} className="text-ab-gold text-sm">{t("nav_logout")}</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate("/connexion")} className="ab-btn-ghost justify-center">{t("nav_login")}</button>
                <button onClick={() => navigate("/adhesion")} className="ab-btn-primary justify-center">{t("nav_join")}</button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Nav;
